import { MaterialBillingAttention } from "@/components/MaterialBillingAttention";
import { DocumentTemplateSelect } from "@/components/DocumentTemplateSelect";
// Full-page invoice workspace — /app/customer-invoices/new and /:iid. NOT a modal.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowLeft, ChevronDown, Check, FileText, Layers, Library, Loader2, Plus, Printer, Send, Eye, Hammer, Package, Truck, Link2, CalendarClock, ExternalLink, Settings, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  CustomerSection, LineItemsTable, AttachmentsSection, NotesSection, ActivitySection,
  SettingsPanel, QuickActionsPanel, ItemLibraryDialog, ImportDocDialog,
} from "@/components/QuoteWorkspaceParts";
import {
  InvoiceDetailsSection, ProgressClaimSection, InvoiceFromSection, DeliveryMethodSection,
  PaymentTermsSection, InvoiceSummaryPanel, PaymentStatusPanel, RecordPaymentDialog,
  RemindersPanel, JobBillingOverview, ImportVariationsDialog, SendInvoiceDialog,
} from "@/components/InvoiceWorkspaceParts";
import { TimesheetHoursImport } from "@/components/TimesheetHoursImport";
import InvoiceDocumentPreview from "@/components/proposal/InvoiceDocumentPreview";

const GST = 0.15;
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE = {
  draft: "bg-zinc-200 text-zinc-700 dark:bg-white/10 dark:text-zinc-300",
  sent: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  viewed: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  partially_paid: "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  paid: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  overdue: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  void: "bg-zinc-200 text-zinc-500 dark:bg-white/10 dark:text-zinc-400",
  cancelled: "bg-zinc-200 text-zinc-500 dark:bg-white/10 dark:text-zinc-400",
};

const INV_SETTINGS_DEFAULT = {
  show_descriptions: true, show_quantity: true, show_unit_price: true, show_discount: true,
  show_tax: true, show_section_subtotals: true, show_bank_details: true, show_payment_terms: true,
  show_customer_reference: true, allow_partial_payments: false,
};
const INV_SETTING_ROWS = [
  ["show_descriptions", "Show Item Descriptions"], ["show_quantity", "Show Quantity"],
  ["show_unit_price", "Show Unit Price"], ["show_discount", "Show Discount"],
  ["show_tax", "Show Tax / GST"], ["show_section_subtotals", "Show Section Subtotals"],
  ["show_bank_details", "Show Bank Details"], ["show_payment_terms", "Show Payment Terms"],
  ["show_customer_reference", "Show Customer Reference"], ["allow_partial_payments", "Allow Partial Payments"],
];

const newItem = (extra = {}) => ({
  description: "", detail: "", qty: 1, unit: "ea", unit_price: 0, discount_pct: 0,
  is_section: false, cost_code: "", category: "", labour_cost: null, material_cost: null,
  plant_cost: null, internal_note: "", ...extra,
});
const newSection = (name) => ({ ...newItem(), description: name, is_section: true, collapsed: false });

export default function InvoiceWorkspace() {
  const { iid } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !iid || iid === "new";

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved");
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [org, setOrg] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [varsOpen, setVarsOpen] = useState(false);
  const [tsImportOpen, setTsImportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(null); // "quote" | "duplicate"
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [mobileSummary, setMobileSummary] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const skipSave = useRef(true);
  const saveTimer = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cs }, { data: js }, { data: o }] = await Promise.all([
          api.get("/clients"), api.get("/jobs"), api.get("/org/me").catch(() => ({ data: null })),
        ]);
        setClients(cs || []); setJobs(js || []); setOrg(o);
        if (isNew) {
          const jobId = params.get("job_id") || null;
          if (jobId) {
            navigate(`/app/jobs/${jobId}/charge-up`, { replace: true });
            return;
          }

          const { data } = await api.post("/customer-invoices", {
            client_id: null,
            client_name: "",
            client_email: "",
            client_address: "",
            job_id: jobId, issue_date: todayISO(),
            settings: INV_SETTINGS_DEFAULT, line_items: [],
          });
          skipSave.current = true;
          setInv({ ...data, settings: { ...INV_SETTINGS_DEFAULT, ...(data.settings || {}) } });
          navigate(`/app/customer-invoices/${data.id}`, { replace: true });
        } else {
          const { data } = await api.get(`/customer-invoices/${iid}`);
          skipSave.current = true;
          setInv({ ...data, settings: { ...INV_SETTINGS_DEFAULT, ...(data.settings || {}) } });
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load invoice");
        navigate("/app/customer-invoices");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iid]);

  const totals = useMemo(() => {
    if (!inv) return { subtotal: 0, discount: 0, retention: 0, net: 0, gst: 0, total: 0 };
    const items = (inv.line_items || []).filter((i) => !i.is_section);
    const lineAmt = (it) => (it.qty || 0) * (it.unit_price || 0) * (1 - Math.min(100, Math.max(0, it.discount_pct || 0)) / 100);
    const subtotal = items.reduce((s, it) => s + lineAmt(it), 0);
    let disc = 0;
    if (inv.discount_type === "percentage" && inv.discount_value) disc = subtotal * Math.min(100, Math.max(0, inv.discount_value)) / 100;
    if (inv.discount_type === "fixed" && inv.discount_value) disc = Math.min(inv.discount_value, subtotal);
    let net = subtotal - disc;
    const ret = inv.retention_pct ? net * Math.min(100, Math.max(0, inv.retention_pct)) / 100 : 0;
    net -= ret;
    let gst, total, exGst;
    if (inv.gst_enabled === false) { exGst = net; gst = 0; total = net; }
    else if (inv.gst_inclusive) { total = net; exGst = net / (1 + GST); gst = total - exGst; }
    else { exGst = net; gst = net * GST; total = net + gst; }
    return { subtotal, discount: disc, retention: ret, net: exGst, gst, total };
  }, [inv]);

  const patch = useCallback((changes) => setInv((prev) => {
    if (prev?.trucking_submission_id && ["line_items", "client_id", "job_id", "gst_inclusive", "discount_type", "discount_value", "retention_pct"].some(key => key in changes)) {
      toast.error("Trucking source quantities, rates and customer context are locked. Commercial amendments require review.");
      return prev;
    }
    return { ...prev, ...changes };
  }), []);

  const buildBody = (i) => ({
    client_id: i.client_id, client_name: i.client_name || "", client_email: i.client_email,
    client_address: i.client_address, issue_date: i.issue_date, due_date: i.due_date, job_id: i.job_id,
    line_items: (i.line_items || []).map((it) => ({ ...it })), notes: i.notes,
    gst_inclusive: !!i.gst_inclusive, gst_enabled: i.gst_enabled !== false, title: i.title, description: i.description, reference: i.reference,
    invoice_type: i.invoice_type, payment_terms: i.payment_terms, settings: i.settings, template_id: i.template_id,
    accent_color: i.accent_color, discount_type: i.discount_type,
    discount_value: i.discount_value === "" ? null : i.discount_value, discount_reason: i.discount_reason,
    retention_pct: i.retention_pct === "" ? null : i.retention_pct, attachments: i.attachments || [],
    advanced: i.advanced || {}, delivery_method: i.delivery_method, reminders: i.reminders || {},
    early_discount: i.early_discount, progress_claim: i.progress_claim,
  });

  useEffect(() => {
    if (!inv?.id) return;
    if (skipSave.current) { skipSave.current = false; return; }
    setSaveState("dirty");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        const { data } = await api.patch(`/customer-invoices/${inv.id}`, buildBody(inv));
        skipSave.current = true;
        setInv((prev) => ({
          ...prev, ...(prev.template_id === inv.template_id && prev.job_id === inv.job_id ? {template_id:data.template_id, template_name:data.template_name, template_source:data.template_source} : {}), subtotal: data.subtotal, discount_amount: data.discount_amount,
          retention_amount: data.retention_amount, gst: data.gst, total: data.total,
          amount_paid: data.amount_paid, credits_applied: data.credits_applied,
          balance_due: data.balance_due, updated_at: data.updated_at,
        }));
        setSaveState("saved");
      } catch { setSaveState("dirty"); }
    }, 1200);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv]);

  const saveNow = async (extra = {}) => {
    clearTimeout(saveTimer.current);
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/customer-invoices/${inv.id}`, { ...buildBody(inv), ...extra });
      skipSave.current = true;
      setInv({ ...data, settings: { ...INV_SETTINGS_DEFAULT, ...(data.settings || {}) } });
      setSaveState("saved");
      return data;
    } catch (e) { setSaveState("dirty"); toast.error(e.response?.data?.detail || "Save failed"); return null; }
  };

  const approveInvoice = async () => {
    const saved = await saveNow({ status: "sent" });
    if (saved) toast.success("Invoice approved — moved to Due", { description: "No email sent. Use Send Invoice to email the customer." });
  };

  const [xeroConnected, setXeroConnected] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  useEffect(() => {
    let ok = true;
    api.get("/xero/status").then(({ data }) => { if (ok) setXeroConnected(!!data?.connected); }).catch(() => {});
    return () => { ok = false; };
  }, []);
  const pushToXero = async () => {
    if (!window.confirm(`Send ${inv.invoice_number} to Xero as a DRAFT?\n\nIt'll appear in your Xero drafts ready for approval — it won't be emailed automatically.`)) return;
    const tid = toast.loading("Sending to Xero…");
    try {
      await saveNow();
      const { data } = await api.post(`/xero/invoices/${inv.id}/push`);
      toast.dismiss(tid);
      toast.success("Sent to Xero as a draft");
      patch({ xero_invoice_id: data.xero_invoice_id, xero_invoice_url: data.xero_invoice_url });
    } catch (e) {
      toast.dismiss(tid);
      const detail = e.response?.data?.detail || "Couldn't send to Xero";
      toast.error(detail.includes("not connected") ? "Connect Xero first under Account → Xero" : detail);
    }
  };

  // Ctrl/Cmd+S
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); saveNow(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv]);

  // Full-screen line items: lock page scroll + Esc to exit
  useEffect(() => {
    if (!fullScreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const h = (e) => { if (e.key === "Escape") setFullScreen(false); };
    window.addEventListener("keydown", h);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", h); };
  }, [fullScreen]);

  const items = inv?.line_items || [];
  const setItems = (next) => patch({ line_items: next });
  const updateItem = (i, ch) => setItems(items.map((it, x) => (x === i ? { ...it, ...ch } : it)));
  const removeItem = (i) => {
    const it = items[i];
    if (it.is_section) {
      let end = i + 1;
      while (end < items.length && !items[end].is_section) end++;
      if (!window.confirm(`Delete section "${it.description}" and its ${end - i - 1} item(s)?`)) return;
      setItems([...items.slice(0, i), ...items.slice(end)]);
    } else setItems(items.filter((_, x) => x !== i));
    setExpandedRow(null);
  };
  const duplicateItem = (i) => setItems([...items.slice(0, i + 1), { ...items[i] }, ...items.slice(i + 1)]);
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };
  const addItem = (extra = {}) => setItems([...items, newItem(extra)]);
  const addItems = (n) => setItems([...items, ...Array.from({ length: n }, () => newItem())]);
  const addSection = () => setItems([...items, newSection(`Section ${items.filter((i) => i.is_section).length + 1}`)]);
  const blockRange = (i) => {
    if (!items[i]?.is_section) return [i, i + 1];
    let end = i + 1;
    while (end < items.length && !items[end].is_section) end++;
    return [i, end];
  };
  const onDrop = (target) => {
    const from = dragRef.current;
    dragRef.current = null;
    if (from == null || from === target) return;
    const [s, e] = blockRange(from);
    if (target > s && target < e) return;
    const block = items.slice(s, e);
    const rest = [...items.slice(0, s), ...items.slice(e)];
    let ins = target > s ? target - (e - s) : target;
    ins = Math.max(0, Math.min(rest.length, ins));
    setItems([...rest.slice(0, ins), ...block, ...rest.slice(ins)]);
  };
  const sectionSubtotals = useMemo(() => {
    const map = {}; let cur = null;
    items.forEach((it, i) => {
      if (it.is_section) { cur = i; map[i] = 0; }
      else if (cur != null) map[cur] += (it.qty || 0) * (it.unit_price || 0) * (1 - (it.discount_pct || 0) / 100);
    });
    return map;
  }, [items]);

  // Merge identical manual/supplier lines (same description, unit, rate) within
  // each section. Tracked labour/plant/site-material lines keep their own line so
  // billing stays traceable.
  const mergeDuplicates = () => {
    const norm = (s) => String(s ?? "").trim().toLowerCase();
    const out = []; let keys = {};
    items.forEach((it) => {
      if (it.is_section) { out.push({ ...it }); keys = {}; return; }
      const tracked = it.source_timesheet_id || it.source_job_material_id || it.source_material_movement_id;
      if (tracked) { out.push({ ...it }); return; }
      const key = `${norm(it.description)}|${norm(it.unit)}|${Number(it.unit_price) || 0}|${Number(it.discount_pct) || 0}`;
      if (key in keys) {
        const tgt = out[keys[key]];
        tgt.qty = Math.round(((Number(tgt.qty) || 0) + (Number(it.qty) || 0)) * 10000) / 10000;
        if (it.source_supplier_items) tgt.source_supplier_items = [...(tgt.source_supplier_items || []), ...it.source_supplier_items];
      } else { keys[key] = out.length; out.push({ ...it }); }
    });
    const removed = items.filter((i) => !i.is_section).length - out.filter((i) => !i.is_section).length;
    if (removed <= 0) { toast.info("No duplicate lines to merge"); return; }
    setItems(out);
    toast.success(`Merged ${removed} duplicate line${removed === 1 ? "" : "s"}`);
  };

  // Auto-organise lines under Labour / Plant & Equipment / Materials headings.
  const groupIntoSections = () => {
    const kindOf = (it) => {
      const c = it.category, sk = it.source_kind;
      if (c === "labour" || sk === "labour") return "Labour";
      if (c === "plant" || sk === "machine") return "Plant & Equipment";
      if (c === "material" || sk === "material") return "Materials";
      return "Other";
    };
    const order = ["Labour", "Plant & Equipment", "Materials", "Other"];
    const content = items.filter((i) => !i.is_section);
    const buckets = {}; content.forEach((it) => { (buckets[kindOf(it)] = buckets[kindOf(it)] || []).push({ ...it }); });
    const groups = order.filter((n) => buckets[n]?.length);
    if (groups.length < 2) { toast.info("All items are the same type — nothing to group"); return; }
    const out = [];
    groups.forEach((name) => { out.push(newSection(name)); out.push(...buckets[name]); });
    setItems(out);
    toast.success("Grouped into sections with subtotals");
  };

  const openPdf = async (print = false) => {
    try {
      const res = await api.get(`/customer-invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (print && w) w.addEventListener("load", () => w.print());
    } catch { toast.error("PDF failed"); }
  };
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/customer-invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
  };
  const uploadFiles = async (files) => {
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is over 15MB`); continue; }
      const fd = new FormData();
      fd.append("file", f); fd.append("category", "invoice_attachments");
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setInv((prev) => ({ ...prev, attachments: [...(prev.attachments || []), { name: f.name, path: data.storage_path, size: f.size }] }));
      } catch { toast.error(`Upload failed: ${f.name}`); }
    }
  };
  const createCreditNote = async () => {
    const bal = inv.balance_due != null ? inv.balance_due : inv.total || 0;
    const amt = window.prompt(`Credit amount (balance due $${bal.toFixed(2)}):`, bal.toFixed(2));
    if (!amt) return;
    const reason = window.prompt("Reason:", `Credit against ${inv.invoice_number}`) || undefined;
    try {
      const { data } = await api.post(`/customer-invoices/${inv.id}/credit-note`, { amount: Number(amt), reason });
      skipSave.current = true;
      setInv((prev) => ({ ...prev, ...data.invoice, settings: { ...INV_SETTINGS_DEFAULT, ...(data.invoice.settings || {}) } }));
      toast.success(`Credit note ${data.credit_note.invoice_number} created`);
    } catch (e) { toast.error(e.response?.data?.detail || "Credit note failed"); }
  };

  if (loading || !inv) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>;
  }

  const status = inv.status || "draft";
  const editable = !["paid", "void", "cancelled"].includes(status);
  const nzd = (n) => new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n || 0);

  const quickActions = [
    { t: "Convert from Quote", d: "Import line items from a quote.", fn: () => setImportOpen("quote"), id: "iqa-quote" },
    { t: "Import Timesheets", d: "Pull unbilled hours from the linked job.", fn: () => (inv.job_id ? setTsImportOpen(true) : toast.error("Link a job first")), id: "iqa-timesheets" },
    { t: "Import Approved Variations", d: "Bill approved variations on the job.", fn: () => (inv.job_id ? setVarsOpen(true) : toast.error("Link a job first")), id: "iqa-variations" },
    { t: "Create Credit Note", d: "Issue a full or partial credit.", fn: createCreditNote, id: "iqa-credit" },
    { t: "Duplicate Invoice", d: "Copy into a new draft invoice.", fn: async () => {
        try {
          const { data } = await api.post("/customer-invoices", buildBody(inv));
          toast.success(`Duplicated as ${data.invoice_number}`);
          navigate(`/app/customer-invoices/${data.id}`);
        } catch { toast.error("Duplicate failed"); }
      }, id: "iqa-duplicate" },
    { t: "Download PDF", d: "Save the customer PDF.", fn: downloadPdf, id: "iqa-pdf" },
  ];

  const lineItemsSection = (
    <section className={fullScreen ? "flex flex-col h-full bg-white dark:bg-[#0F141C]" : "border-2 border-zinc-300 dark:border-white/15 bg-white dark:bg-[#0F141C]"} data-testid="invoice-line-items">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.03] shrink-0">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600 dark:text-zinc-300">
          <Layers className="w-3.5 h-3.5 text-[#FF5F15]" /> Line Items
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setLibraryOpen(true)} className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="invoice-add-from-library">
            <Library className="w-3 h-3 mr-1" /> Add from Library
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-7 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase tracking-wider" data-testid="invoice-add-item-menu">
                <Plus className="w-3 h-3 mr-1" /> Add Item <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => addItem()} data-testid="inv-add-line-item">Add Line Item</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addItem({ category: "labour", unit: "hr" })}><Hammer className="w-3.5 h-3.5 mr-2" /> Add Labour</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addItem({ category: "material" })}><Package className="w-3.5 h-3.5 mr-2" /> Add Material</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addItem({ category: "plant", unit: "hr" })}><Truck className="w-3.5 h-3.5 mr-2" /> Add Plant / Equipment</DropdownMenuItem>
              <DropdownMenuItem onClick={addSection} data-testid="inv-add-section-menu"><Layers className="w-3.5 h-3.5 mr-2" /> Add Section</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={groupIntoSections} data-testid="inv-group-sections"><Layers className="w-3.5 h-3.5 mr-2" /> Group into Labour / Plant / Materials</DropdownMenuItem>
              <DropdownMenuItem onClick={mergeDuplicates} data-testid="inv-merge-duplicates"><Package className="w-3.5 h-3.5 mr-2" /> Merge duplicate lines</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setImportOpen("quote")} data-testid="inv-import-quote">Import from Quote</DropdownMenuItem>
              <DropdownMenuItem onClick={() => (inv.job_id ? setTsImportOpen(true) : toast.error("Link a job first (Invoice Details → Additional Details)"))} data-testid="inv-import-timesheets">Import Timesheet Hours</DropdownMenuItem>
              <DropdownMenuItem onClick={() => (inv.job_id ? setVarsOpen(true) : toast.error("Link a job first (Invoice Details → Additional Details)"))} data-testid="inv-import-variations">Import Approved Variations</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="outline" onClick={() => setFullScreen((f) => !f)}
            className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15"
            title={fullScreen ? "Exit full screen (Esc)" : "Edit line items full screen"} data-testid="invoice-fullscreen-toggle">
            {fullScreen ? <><Minimize2 className="w-3 h-3 mr-1" /> Exit full screen</> : <><Maximize2 className="w-3 h-3 mr-1" /> Full screen</>}
          </Button>
        </div>
      </div>
      <div className={fullScreen ? "flex-1 overflow-auto" : ""}>
        <LineItemsTable
          items={items} updateItem={updateItem} removeItem={removeItem} duplicateItem={duplicateItem}
          moveItem={moveItem} expandedRow={expandedRow} setExpandedRow={setExpandedRow}
          sectionSubtotals={sectionSubtotals} settings={inv.settings} dragRef={dragRef} onDrop={onDrop} editable={editable}
        />
        <div className="m-3 flex flex-wrap items-center gap-2" data-testid="invoice-add-row">
          <button onClick={() => { addItem(); setTimeout(() => { const el = document.querySelectorAll('[data-testid^="item-desc-"]'); el[el.length - 1]?.focus(); }, 60); }}
            className="flex-1 min-w-[160px] border-2 border-dashed border-zinc-300 dark:border-white/15 py-2.5 text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:border-[#FF5F15] hover:text-[#FF5F15] hover:bg-[#FF5F15]/5 transition-colors flex items-center justify-center gap-1.5"
            data-testid="invoice-add-line"><Plus className="w-3.5 h-3.5" /> Add a line</button>
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            <span className="px-1">Add</span>
            {[5, 10, 20].map((n) => <button key={n} onClick={() => addItems(n)} data-testid={`invoice-add-${n}-lines`}
              className="h-9 w-9 rounded-sm border border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300 hover:border-[#FF5F15] hover:text-[#FF5F15] font-bold">{n}</button>)}
          </div>
          <button onClick={addSection}
            className="border-2 border-dashed border-zinc-300 dark:border-white/15 py-2.5 px-5 text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:border-[#FF5F15] hover:text-[#FF5F15] hover:bg-[#FF5F15]/5 transition-colors flex items-center justify-center gap-1.5"
            data-testid="invoice-add-section"><Layers className="w-3.5 h-3.5" /> Add section</button>
        </div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen" data-testid="invoice-workspace">
      <div className="px-4 lg:px-8"><MaterialBillingAttention jobId={inv.job_id} preview /></div>
      <div className="px-4 lg:px-8 py-3 max-w-lg"><DocumentTemplateSelect kind="invoice" value={inv.template_id} disabled={status !== "draft"} onChange={(template_id, config) => patch({template_id, ...(!inv.line_items?.length && config?.line_items?.length ? {line_items:config.line_items.map(item => ({...item}))} : {})})} /><p className="mt-1 text-xs text-muted-foreground">{status === "draft" ? "Applies to this document only. Job defaults are unchanged." : "Template locked after issue."}</p></div>
      {inv.trucking_submission_id && <div className="px-4 lg:px-8 py-3 bg-orange-50 dark:bg-orange-950/20 text-sm">Created from trucking load evidence. Source quantities, rates and customer context are locked; invoice delivery and payment controls remain available. Claim release and source amendments require review.</div>}
      {/* sticky header */}
      <div className="sticky top-0 z-30 border-b border-zinc-200 dark:border-white/10 bg-white/95 dark:bg-[#0B0F14]/95 backdrop-blur px-4 lg:px-8 py-3" data-testid="invoice-header">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <button onClick={() => navigate("/app/customer-invoices")} className="text-zinc-400 hover:text-[#FF5F15]" data-testid="invoice-back">
                <ArrowLeft className="w-4.5 h-4.5" />
              </button>
              <h1 className="font-display font-black uppercase tracking-tight text-xl lg:text-2xl">{inv.invoice_number || "Create Invoice"}</h1>
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE.draft}`} data-testid="invoice-status-badge">{status === "sent" ? "Due" : status.replace("_", " ")}</span>
              <span className="hidden md:inline text-[11px] text-zinc-400" data-testid="invoice-autosave-state">
                {saveState === "saved" ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> All changes saved</span>
                  : saveState === "saving" ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                  : "Unsaved changes"}
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 mt-0.5 ml-7 hidden sm:block">Create and send a professional invoice to your customer.</p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            {status === "draft" && <Button size="sm" disabled={saveState === "saving"} onClick={approveInvoice} title="Move this invoice to Due without emailing the customer" className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold text-[11px]" data-testid="invoice-approve"><Check className="w-3.5 h-3.5 mr-1.5" />Approve invoice</Button>}
            <Button variant="outline" size="sm" onClick={() => saveNow()} className="h-8 rounded-sm uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15" data-testid="invoice-save-draft">{status === "draft" ? "Save as Draft" : "Save"}</Button>
            <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)} className="h-8 rounded-sm uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15" data-testid="invoice-preview">
              <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[11px]" data-testid="invoice-send-menu">
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Send Invoice <ChevronDown className="w-3 h-3 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem onClick={() => setSendOpen(true)} data-testid="invoice-send-now"><Send className="w-3.5 h-3.5 mr-2" /> Send Invoice</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await saveNow(); toast.success("Saved — send whenever you're ready"); }}><CalendarClock className="w-3.5 h-3.5 mr-2" /> Save &amp; Send Later</DropdownMenuItem>
                {status === "draft" && (
                  <DropdownMenuItem onClick={approveInvoice} disabled={saveState === "saving"} data-testid="invoice-mark-sent"><Check className="w-3.5 h-3.5 mr-2" /> Approve invoice (no email)</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {xeroConnected && (inv.xero_invoice_id
                  ? <DropdownMenuItem onClick={() => window.open(inv.xero_invoice_url || `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${inv.xero_invoice_id}`, "_blank")} data-testid="invoice-open-xero"><ExternalLink className="w-3.5 h-3.5 mr-2" /> Open in Xero</DropdownMenuItem>
                  : <DropdownMenuItem onClick={pushToXero} data-testid="invoice-send-xero"><ExternalLink className="w-3.5 h-3.5 mr-2" /> Send draft to Xero</DropdownMenuItem>)}
                <DropdownMenuItem onClick={downloadPdf} data-testid="invoice-download-pdf"><FileText className="w-3.5 h-3.5 mr-2" /> Download PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openPdf(true)}><Printer className="w-3.5 h-3.5 mr-2" /> Print Invoice</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/app/customer-invoices/${inv.id}`); toast.success("Invoice link copied"); }}><Link2 className="w-3.5 h-3.5 mr-2" /> Copy Invoice Link</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-8 py-5 pb-28 lg:pb-10 w-full space-y-4" data-testid="invoice-body">
        <CustomerSection q={inv} patch={patch} clients={clients} setClients={setClients} editable={editable} title="Bill To" />
        <InvoiceDetailsSection inv={inv} patch={patch} advancedOpen={advancedOpen} setAdvancedOpen={setAdvancedOpen} jobs={jobs} />
        {inv.invoice_type === "progress_claim" && <ProgressClaimSection inv={inv} patch={patch} liveSubtotal={totals.subtotal - totals.discount} />}

        {/* LINE ITEMS */}
        {fullScreen ? (
          <div className="border-2 border-dashed border-[#FF5F15]/40 bg-[#FF5F15]/5 px-4 py-6 text-center" data-testid="invoice-line-items-placeholder">
            <p className="text-sm font-semibold text-zinc-600 dark:text-zinc-300">Line items are open in full screen</p>
            <Button size="sm" variant="outline" onClick={() => setFullScreen(false)} className="mt-3 h-8 rounded-sm text-[11px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="invoice-fullscreen-return">
              <Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Return to page
            </Button>
          </div>
        ) : lineItemsSection}

        {/* TOTALS */}
        <InvoiceSummaryPanel totals={totals} inv={inv} patch={patch} onPreview={() => setPreviewOpen(true)} onSend={() => setSendOpen(true)} />

        {/* Show only when there's something to act on */}
        {status !== "draft" && <PaymentStatusPanel inv={inv} onRecord={() => setPayOpen(true)} />}
        {inv.job_id && <JobBillingOverview inv={inv} liveTotal={totals.total} />}

        {/* Everything else tucked away, Xero-style */}
        <div className="border border-zinc-200 dark:border-white/10 rounded-sm bg-white dark:bg-[#0F141C]">
          <button onClick={() => setOptionsOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-[11px] font-bold uppercase tracking-widest text-zinc-600 dark:text-zinc-300" data-testid="invoice-options-toggle">
            <span className="inline-flex items-center gap-2"><Settings className="w-3.5 h-3.5 text-[#FF5F15]" /> Invoice options &amp; settings</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${optionsOpen ? "rotate-180" : ""}`} />
          </button>
          {optionsOpen && <div className="px-4 pb-4 pt-4 space-y-4 border-t border-zinc-200 dark:border-white/10" data-testid="invoice-options-body">
            <SettingsPanel q={inv} patch={patch} rows={INV_SETTING_ROWS} title="Invoice Settings" accentLabel="Invoice Accent Colour"
              extra={
                <div className="mt-4 pt-3 border-t border-zinc-100 dark:border-white/5">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400">Retention %</div>
                  <input type="number" step="0.5" min="0" max="100" value={inv.retention_pct ?? ""}
                    onChange={(e) => patch({ retention_pct: e.target.value === "" ? null : Number(e.target.value) })}
                    placeholder="e.g. 5"
                    className="mt-2 w-24 h-8 rounded-sm border border-zinc-300 dark:border-white/15 dark:bg-white/5 text-sm text-right px-2 bg-transparent"
                    data-testid="retention-pct-input" />
                  <p className="text-[10px] text-zinc-400 mt-1.5">Held back from this claim before GST. Leave blank for none.</p>
                </div>
              } />
            <RemindersPanel inv={inv} patch={patch} />
            <DeliveryMethodSection inv={inv} patch={patch} />
            <PaymentTermsSection inv={inv} patch={patch} org={org} />
            <InvoiceFromSection org={org} inv={inv} />
            <AttachmentsSection q={inv} setQ={setInv} uploadFiles={uploadFiles} />
            <NotesSection q={inv} patch={patch} />
            <QuickActionsPanel actions={quickActions} />
          </div>}
        </div>

        <ActivitySection q={inv} setQ={setInv} noteUrl={`/customer-invoices/${inv.id}/note`} />
      </div>

      {/* mobile bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0B0F14] p-2.5" data-testid="invoice-mobile-bar">
        <button onClick={() => setMobileSummary((s) => !s)} className="w-full flex items-center justify-between px-1 pb-2 text-xs">
          <span className="font-bold uppercase tracking-wider text-zinc-500">Balance Due</span>
          <span className="font-mono font-black text-[#FF5F15]">{nzd(inv.balance_due != null ? inv.balance_due : totals.total)} <ChevronDown className={`inline w-3.5 h-3.5 transition-transform ${mobileSummary ? "" : "rotate-180"}`} /></span>
        </button>
        {mobileSummary && (
          <div className="px-1 pb-2 text-xs space-y-1 text-zinc-500">
            <div className="flex justify-between"><span>Total</span><span className="font-mono">{nzd(totals.total)}</span></div>
            <div className="flex justify-between"><span>Paid</span><span className="font-mono">{nzd(inv.amount_paid || 0)}</span></div>
            <button onClick={() => setPayOpen(true)} className="w-full mt-1 py-2 border border-zinc-300 dark:border-white/15 text-[10px] font-bold uppercase tracking-wider">$ Record Payment</button>
          </div>
        )}
        {status === "draft" && <Button disabled={saveState === "saving"} onClick={approveInvoice} className="w-full mb-2 rounded-sm h-9 bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold" title="Move to Due without emailing the customer" data-testid="invoice-approve-mobile"><Check className="w-4 h-4 mr-1.5" />Approve invoice</Button>}
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" onClick={() => saveNow()} className="rounded-sm h-9 text-[11px] font-bold uppercase tracking-wider">Save</Button>
          <Button variant="outline" onClick={() => setPreviewOpen(true)} className="rounded-sm h-9 text-[11px] font-bold uppercase tracking-wider">Preview</Button>
          <Button onClick={() => setSendOpen(true)} className="rounded-sm h-9 bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider">Send</Button>
        </div>
      </div>

      {fullScreen && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-zinc-100 dark:bg-[#0B0F14]" data-testid="invoice-fullscreen-overlay">
          <div className="shrink-0 flex items-center justify-between gap-3 px-4 lg:px-6 py-3 border-b border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]">
            <div className="flex items-center gap-3 min-w-0">
              <Layers className="w-4 h-4 text-[#FF5F15] shrink-0" />
              <span className="font-display font-black uppercase tracking-tight text-sm lg:text-base truncate">{inv.invoice_number || "Invoice"} — Line Items</span>
              <span className="hidden md:inline text-[11px] text-zinc-400">
                {saveState === "saved" ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Saved</span>
                  : saveState === "saving" ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                  : "Unsaved changes"}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-wider text-zinc-400">Total</span>
              <span className="font-mono font-black text-[#FF5F15] text-sm lg:text-base">{nzd(totals.total)}</span>
              <Button size="sm" onClick={() => setFullScreen(false)} className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="invoice-fullscreen-done">
                <Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Done
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden p-3 lg:p-5">
            <div className="h-full border-2 border-zinc-300 dark:border-white/15 flex flex-col overflow-hidden bg-white dark:bg-[#0F141C]">
              {lineItemsSection}
            </div>
          </div>
        </div>
      )}

      <ItemLibraryDialog open={libraryOpen} onClose={() => setLibraryOpen(false)} onAdd={(picked) => { setItems([...items, ...picked]); setLibraryOpen(false); }} />
      <InvoiceDocumentPreview open={previewOpen} onClose={() => setPreviewOpen(false)} invoiceId={inv.id} saveNow={saveNow} />

      <SendInvoiceDialog open={sendOpen} onClose={() => setSendOpen(false)} inv={inv} totals={totals} saveNow={saveNow}
        onSent={(data) => { skipSave.current = true; setInv((prev) => ({ ...prev, ...data })); }} />
      <RecordPaymentDialog open={payOpen} onClose={() => setPayOpen(false)} inv={inv}
        onSaved={(data) => { skipSave.current = true; setInv((prev) => ({ ...prev, ...data, settings: { ...INV_SETTINGS_DEFAULT, ...(data.settings || {}) } })); }} />
      <ImportVariationsDialog open={varsOpen} onClose={() => setVarsOpen(false)} jobId={inv.job_id}
        onAdd={(lines) => setItems([...items, ...lines])} />
      {tsImportOpen && (
        <TimesheetHoursImport open={tsImportOpen} onOpenChange={setTsImportOpen} jobId={inv.job_id}
          existingIds={new Set(items.filter((li) => li.source_timesheet_id).map((li) => `${li.source_timesheet_id}|${li.source_kind === "machine" ? "m" : "l"}`))}
          cutoffDay={org?.invoice_cutoff_day}
          onAdd={(lines) => { setItems([...items, ...lines.map((l) => ({ ...newItem(), ...l }))]); setTsImportOpen(false); }} />
      )}
      <ImportDocDialog mode={importOpen} onClose={() => setImportOpen(null)} currentId={inv.id}
        onPick={(doc) => {
          setItems([...items, ...((doc.line_items || []).map((li) => ({ ...newItem(), ...li })))]);
          patch({ from_quote_id: doc.id });
          toast.success(`${(doc.line_items || []).length} line(s) imported from ${doc.quote_number}`);
          setImportOpen(null);
        }} />
    </div>
  );
}
